import React, { useState, useEffect } from 'react';
import NotificationPollingService from 'services/notificationPollingService';

interface NotificationPermissionProps {
  onPermissionChange?: (granted: boolean) => void;
  showAlways?: boolean;
}

const NotificationPermission: React.FC<NotificationPermissionProps> = ({ 
  onPermissionChange, 
  showAlways = false 
}) => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const checkNotificationSupport = () => {
      const supported = 'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        setShowBanner(Notification.permission === 'default' || showAlways);
      }
    };

    checkNotificationSupport();
  }, [showAlways]);

  const requestPermission = async () => {
    try {
      const notificationService = NotificationPollingService.getInstance();
      const granted = await notificationService.requestNotificationPermission();
      
      setPermission(granted ? 'granted' : 'denied');
      setShowBanner(false);
      
      if (onPermissionChange) {
        onPermissionChange(granted);
      }

      if (granted) {
        // Start polling for notifications
        await notificationService.startPolling();
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setPermission('denied');
    }
  };

  const dismissBanner = () => {
    setShowBanner(false);
  };

  // Automatically request permission and start polling when component mounts
  useEffect(() => {
    const initializeNotifications = async () => {
      console.log('NotificationPermission: Initializing notifications', { isSupported, permission });
      
      if (isSupported && permission === 'default') {
        console.log('NotificationPermission: Requesting permission');
        await requestPermission();
      } else if (isSupported && permission === 'granted') {
        // If permission is already granted, just start polling
        console.log('NotificationPermission: Starting polling service');
        const notificationService = NotificationPollingService.getInstance();
        await notificationService.startPolling();
        
        // Test notification fetch after a short delay
        setTimeout(() => {
          console.log('NotificationPermission: Testing notification fetch');
          notificationService.refreshNotifications();
        }, 2000);
      }
    };

    initializeNotifications();
  }, [isSupported, permission]);

  // Return null to keep it hidden but functional
  return null;
};

export default NotificationPermission; 